import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Graba audio del micrófono y lo devuelve como WAV PCM mono a 16 kHz (válido para cualquier navegador y para
 * la transcripción del back). Unos 32 KB por segundo: 60 s ≈ 2 MB.
 *
 *   const { soportado, grabando, segundos, preparar, iniciar, detener, cancelar } = useGrabadorAudio({ maxSegundos, onLimite });
 *   preparar();                      // opcional (hover): abre ya el micrófono si hay permiso, para no perder el principio
 *   await iniciar();                 // pide permiso la primera vez; lanza Error con .codigo MIC_DENEGADO / MIC_NO_DISPONIBLE
 *                                    // `grabando` pasa a true cuando de verdad está grabando
 *   const wav = await detener();     // Blob audio/wav (o null si no había nada o no se oye voz)
 *   cancelar();                      // descarta la grabación
 */
const FRECUENCIA = 16000;

const crearError = (codigo) => Object.assign(new Error(codigo), { codigo });

const escribirTexto = (vista, pos, texto) => {
  for (let i = 0; i < texto.length; i++) vista.setUint8(pos + i, texto.charCodeAt(i));
};

// Silencio al principio del WAV: si el audio empieza de golpe, la transcripción suele perder la primera palabra
const SILENCIO_INICIAL_S = 0.3;
// Micrófono abierto "en caliente" (al pasar el ratón por el botón) que se suelta si no se llega a grabar
const CALENTAMIENTO_MS = 15000;
// Al abrir el micrófono, el navegador da la pista por iniciada pero durante un rato solo entrega ceros (más la primera
// vez, tras conceder el permiso). "Grabando" no se muestra hasta que llega señal real, más un margen:
const ESPERA_SENAL_MAX_MS = 2000;
const MARGEN_PERMISO_NUEVO_MS = 1500; // acaba de conceder el permiso: el micro y su ajuste automático tardan más
const MARGEN_MICRO_NUEVO_MS = 500; // micro recién abierto (sin precalentar al pasar el ratón)

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Espera a que el micrófono entregue señal de verdad (no ceros). Con tope: si no se puede medir, sigue.
const esperarSenal = async (stream) => {
  const Contexto = window.AudioContext || window.webkitAudioContext;
  let contexto;
  try {
    contexto = new Contexto();
    const analizador = contexto.createAnalyser();
    analizador.fftSize = 2048;
    contexto.createMediaStreamSource(stream).connect(analizador);
    const datos = new Float32Array(analizador.fftSize);
    const limite = Date.now() + ESPERA_SENAL_MAX_MS;
    while (Date.now() < limite) {
      if (contexto.state === "suspended") await contexto.resume().catch(() => {});
      analizador.getFloatTimeDomainData(datos);
      if (datos.some((v) => Math.abs(v) > 1e-4)) return;
      await esperar(50);
    }
  } catch {
    // Sin Web Audio: se sigue sin esperar
  } finally {
    contexto?.close?.();
  }
};

// Float32 [-1, 1] -> WAV PCM 16 bits mono
const codificarWav = (audio, frecuencia) => {
  const muestras = new Float32Array(Math.round(SILENCIO_INICIAL_S * frecuencia) + audio.length);
  muestras.set(audio, muestras.length - audio.length);
  const vista = new DataView(new ArrayBuffer(44 + muestras.length * 2));
  escribirTexto(vista, 0, "RIFF");
  vista.setUint32(4, 36 + muestras.length * 2, true);
  escribirTexto(vista, 8, "WAVE");
  escribirTexto(vista, 12, "fmt ");
  vista.setUint32(16, 16, true); // tamaño del chunk fmt
  vista.setUint16(20, 1, true); // PCM
  vista.setUint16(22, 1, true); // mono
  vista.setUint32(24, frecuencia, true);
  vista.setUint32(28, frecuencia * 2, true); // byte rate
  vista.setUint16(32, 2, true); // block align
  vista.setUint16(34, 16, true); // bits por muestra
  escribirTexto(vista, 36, "data");
  vista.setUint32(40, muestras.length * 2, true);
  for (let i = 0; i < muestras.length; i++) {
    const s = Math.max(-1, Math.min(1, muestras[i]));
    vista.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([vista], { type: "audio/wav" });
};

// ¿Hay voz? Al menos ~150 ms (3 ventanas de 50 ms) con nivel de voz; si no, es silencio o ruido de fondo
const VENTANA = FRECUENCIA / 20;
const UMBRAL_RMS = 0.01;
const VENTANAS_CON_VOZ = 3;
const hayVoz = (muestras) => {
  let conVoz = 0;
  for (let inicio = 0; inicio + VENTANA <= muestras.length; inicio += VENTANA) {
    let suma = 0;
    for (let i = inicio; i < inicio + VENTANA; i++) suma += muestras[i] * muestras[i];
    if (Math.sqrt(suma / VENTANA) > UMBRAL_RMS && ++conVoz >= VENTANAS_CON_VOZ) return true;
  }
  return false;
};

// Cualquier formato que grabe el navegador (webm, ogg, mp4) -> WAV 16 kHz mono (null si no hay voz)
const aWav = async (blob) => {
  const Contexto = window.AudioContext || window.webkitAudioContext;
  const contexto = new Contexto();
  let audio;
  try {
    audio = await contexto.decodeAudioData(await blob.arrayBuffer());
  } finally {
    contexto.close?.();
  }
  const fotogramas = Math.max(1, Math.ceil(audio.duration * FRECUENCIA));
  const offline = new OfflineAudioContext(1, fotogramas, FRECUENCIA);
  const fuente = offline.createBufferSource();
  fuente.buffer = audio;
  fuente.connect(offline.destination);
  fuente.start();
  const renderizado = await offline.startRendering();
  const muestras = renderizado.getChannelData(0);
  return hayVoz(muestras) ? codificarWav(muestras, FRECUENCIA) : null;
};

export const grabacionSoportada = () =>
  typeof window !== "undefined"
  && !!navigator.mediaDevices?.getUserMedia
  && typeof window.MediaRecorder !== "undefined"
  && !!(window.AudioContext || window.webkitAudioContext)
  && typeof window.OfflineAudioContext !== "undefined";

export default function useGrabadorAudio({ maxSegundos = 60, onLimite } = {}) {
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const grabador = useRef(null);
  const flujo = useRef(null);
  const trozos = useRef([]);
  const inicio = useRef(0);
  const reloj = useRef(null);
  const alLimite = useRef(onLimite);
  alLimite.current = onLimite;

  const calentamiento = useRef(null);

  const liberar = useCallback(() => {
    clearInterval(reloj.current);
    reloj.current = null;
    clearTimeout(calentamiento.current);
    flujo.current?.getTracks().forEach((pista) => pista.stop());
    flujo.current = null;
    grabador.current = null;
    setGrabando(false);
    setSegundos(0);
  }, []);

  // Al desmontar, soltar el micrófono
  useEffect(() => () => {
    try { grabador.current?.state === "recording" && grabador.current.stop(); } catch { /* ya parado */ }
    flujo.current?.getTracks().forEach((pista) => pista.stop());
    clearInterval(reloj.current);
    clearTimeout(calentamiento.current);
  }, []);

  const abrirMicrofono = () =>
    navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
  const microfonoAbierto = () => flujo.current?.getAudioTracks().some((pista) => pista.readyState === "live");

  /**
   * Abre el micrófono antes de pulsar (al pasar el ratón o enfocar el botón) para que la grabación empiece al instante.
   * Solo si el permiso ya está concedido: nunca saca el aviso del navegador por un hover. Si no se graba, se suelta.
   */
  const preparar = useCallback(async () => {
    if (!grabacionSoportada() || grabador.current || microfonoAbierto()) return;
    try {
      const permiso = await navigator.permissions?.query({ name: "microphone" });
      if (permiso?.state !== "granted") return;
      const stream = await abrirMicrofono();
      if (grabador.current || microfonoAbierto()) {
        stream.getTracks().forEach((pista) => pista.stop());
        return;
      }
      flujo.current = stream;
      clearTimeout(calentamiento.current);
      calentamiento.current = setTimeout(() => {
        if (grabador.current) return;
        flujo.current?.getTracks().forEach((pista) => pista.stop());
        flujo.current = null;
      }, CALENTAMIENTO_MS);
    } catch {
      // Sin Permissions API o sin micrófono: se abrirá al pulsar
    }
  }, []);

  const iniciar = useCallback(async () => {
    if (!grabacionSoportada()) throw crearError("MIC_NO_DISPONIBLE");
    if (grabador.current) return;
    clearTimeout(calentamiento.current);
    let stream = microfonoAbierto() ? flujo.current : null;
    const precalentado = !!stream;
    let permisoNuevo = false;
    if (!stream) {
      try {
        const permiso = await navigator.permissions?.query({ name: "microphone" }).catch(() => null);
        permisoNuevo = !!permiso && permiso.state !== "granted";
        stream = await abrirMicrofono();
      } catch (error) {
        throw crearError(error?.name === "NotAllowedError" || error?.name === "SecurityError" ? "MIC_DENEGADO" : "MIC_NO_DISPONIBLE");
      }
    }
    flujo.current = stream;
    trozos.current = [];
    const rec = new MediaRecorder(stream);
    rec.ondataavailable = (e) => { if (e.data?.size) trozos.current.push(e.data); };
    grabador.current = rec;
    // Se graba desde ya (no se pierde nada de lo que llegue), pero "Grabando" solo se muestra cuando el grabador ha
    // arrancado (con tope por si el navegador no emite onstart), el micrófono entrega señal real y ha pasado el margen
    await new Promise((resolve) => {
      const tope = setTimeout(resolve, 1000);
      rec.onstart = () => { clearTimeout(tope); resolve(); };
      rec.start(250);
    });
    if (!precalentado) {
      await esperarSenal(stream);
      await esperar(permisoNuevo ? MARGEN_PERMISO_NUEVO_MS : MARGEN_MICRO_NUEVO_MS);
    }
    // Cancelado mientras se preparaba
    if (grabador.current !== rec) return;
    inicio.current = Date.now();
    setGrabando(true);
    setSegundos(0);
    reloj.current = setInterval(() => {
      const s = Math.floor((Date.now() - inicio.current) / 1000);
      setSegundos(s);
      if (s >= maxSegundos) alLimite.current?.();
    }, 250);
  }, [maxSegundos]);

  // Para la grabación y espera a que el navegador entregue el último trozo
  const parar = useCallback(() => new Promise((resolve) => {
    const rec = grabador.current;
    if (!rec || rec.state === "inactive") return resolve(null);
    rec.onstop = () => resolve(new Blob(trozos.current, { type: rec.mimeType || "audio/webm" }));
    try {
      rec.stop();
    } catch {
      resolve(null);
    }
  }), []);

  const detener = useCallback(async () => {
    const blob = await parar();
    liberar();
    if (!blob || !blob.size) return null;
    return aWav(blob);
  }, [parar, liberar]);

  const cancelar = useCallback(() => {
    parar().finally(liberar);
    trozos.current = [];
  }, [parar, liberar]);

  return { soportado: grabacionSoportada(), grabando, segundos, preparar, iniciar, detener, cancelar };
}
